/**
 * The activity/audit data model — pure functions, no React, no I/O.
 *
 * Every activity surface in the app (the audit log panel, the per-page
 * activity panes, the "Recent Activity" cards) used to do its own reading of
 * the backend's audit rows: its own user-name assembly, its own "is this a
 * string or an object?" dance over `details`, its own diff rendering, its own
 * action→label table. They disagreed, so the same row read differently
 * depending on which page you opened it from.
 *
 * This file is the one reading. A raw audit row goes in, a normalized
 * `ActivityEntry` comes out, and the display layer only has to draw it. It is
 * pure and covered by tests because it is where a rewrite can silently start
 * showing a user raw JSON — or the wrong verb for a destructive action.
 */

import { isOpaqueId } from '../../../utils/identifiers';

/** The visual family an action belongs to — drives glyph and badge colour. */
export type ActivityKind =
  | 'created'
  | 'updated'
  | 'deleted'
  | 'archived'
  | 'restored'
  | 'completed'
  | 'generated'
  | 'access'
  | 'link'
  | 'unlink'
  | 'share'
  | 'comment'
  | 'attachment'
  | 'document'
  | 'other';

/** One row on an activity timeline, ready to draw. */
export interface ActivityEntry {
  id: string;
  /** Display name of who did it. Empty when the backend attributes it to no one. */
  actor: string;
  /**
   * The actor's email, when the host can resolve one.
   *
   * Audit rows carry a name and nothing else, so this is never filled in by
   * `toActivityEntry` — a host that already holds a people directory (the
   * administration dashboard holds the org's users) attaches it so the avatar
   * can resolve past initials to a real picture.
   */
  actorEmail?: string;
  /**
   * The actor's profile picture, when the host can resolve one.
   *
   * Same story as `actorEmail`, one step further along `ProfileImage`'s
   * chain: audit rows carry a name and nothing else, so nothing here fills
   * this in — a host that KNOWS whose activity it is renders (the dashboard
   * feed is "what you changed", so the actor is the signed-in user) attaches
   * it, and the avatar shows a face instead of two letters.
   */
  actorAvatar?: string | null;
  /** Human-readable verb — "Created", "Access granted". Already de-slugged. */
  action: string;
  /** What it happened to: a subject, a diff summary, a note. */
  target?: string;
  /** ISO-ish timestamp; rendered through `feedTime`. */
  timestamp?: string;
  kind: ActivityKind;
  /** The row it came from — for hosts that act on a click (jump to a finding). */
  raw?: unknown;
}

export interface AuditDiff {
  field: string;
  label: string;
  from?: any;
  to?: any;
}

/** A raw audit row, in every shape the backend endpoints return one. */
export interface AuditRecord {
  id: number | string;
  action: string;
  entity_type?: string;
  user_first_name?: string | null;
  user_last_name?: string | null;
  user_name?: string | null;
  timestamp?: string | null;
  created_at?: string | null;
  notes?: string | null;
  details?: string | Record<string, any> | null;
  details_json?: Record<string, any> | null;
  before_data?: Record<string, any> | null;
  after_data?: Record<string, any> | null;
  differences?: AuditDiff[] | Record<string, any> | null;
}

// ─── Action vocabulary ───────────────────────────────────────────────────
// Known actions get an explicit verb; everything else is inferred and
// de-slugged below. The old table also carried a Tailwind text/bg colour pair
// per action — 60 hardcoded palette strings that dark mode had to be told
// about one at a time. Colour now comes from `kind`, once.

interface ActionSpec {
  kind: ActivityKind;
  label: string;
}

const ACTIONS: Record<string, ActionSpec> = {
  create: { kind: 'created', label: 'Created' },
  created: { kind: 'created', label: 'Created' },
  matter_create: { kind: 'created', label: 'Created' },
  task_create: { kind: 'created', label: 'Created' },
  draft_create: { kind: 'created', label: 'Created' },
  insight_create: { kind: 'created', label: 'Created' },
  update: { kind: 'updated', label: 'Updated' },
  updated: { kind: 'updated', label: 'Updated' },
  matter_update: { kind: 'updated', label: 'Updated' },
  task_update: { kind: 'updated', label: 'Updated' },
  draft_update: { kind: 'updated', label: 'Updated' },
  insight_update: { kind: 'updated', label: 'Updated' },
  stage_update: { kind: 'updated', label: 'Stage changed' },
  status_update: { kind: 'updated', label: 'Status changed' },
  delete: { kind: 'deleted', label: 'Deleted' },
  deleted: { kind: 'deleted', label: 'Deleted' },
  matter_delete: { kind: 'deleted', label: 'Deleted' },
  task_delete: { kind: 'deleted', label: 'Deleted' },
  draft_delete: { kind: 'deleted', label: 'Deleted' },
  insight_delete: { kind: 'deleted', label: 'Deleted' },
  archive: { kind: 'archived', label: 'Archived' },
  archived: { kind: 'archived', label: 'Archived' },
  task_archive: { kind: 'archived', label: 'Archived' },
  restore: { kind: 'restored', label: 'Restored' },
  restored: { kind: 'restored', label: 'Restored' },
  draft_restore: { kind: 'restored', label: 'Restored version' },
  insight_restore: { kind: 'restored', label: 'Restored version' },
  complete: { kind: 'completed', label: 'Completed' },
  completed: { kind: 'completed', label: 'Completed' },
  source_add: { kind: 'link', label: 'Source added' },
  source_remove: { kind: 'unlink', label: 'Source removed' },
  document_upload: { kind: 'document', label: 'Document uploaded' },
  attachment_add: { kind: 'attachment', label: 'Attachment added' },
  attachment_remove: { kind: 'attachment', label: 'Attachment removed' },
  draft_file_upload: { kind: 'attachment', label: 'Attachment added' },
  draft_file_delete: { kind: 'attachment', label: 'Attachment removed' },
  comment_add: { kind: 'comment', label: 'Comment added' },
  collaborator_add: { kind: 'access', label: 'Access granted' },
  collaborator_added: { kind: 'access', label: 'Access granted' },
  collaborator_remove: { kind: 'access', label: 'Access removed' },
  collaborator_removed: { kind: 'access', label: 'Access removed' },
  collaborator_role_update: { kind: 'access', label: 'Access changed' },
  collaborator_access_changed: { kind: 'access', label: 'Access changed' },
  client_add: { kind: 'access', label: 'Client added' },
  client_remove: { kind: 'access', label: 'Client removed' },
  client_role_update: { kind: 'access', label: 'Client role changed' },
  matter_link: { kind: 'link', label: 'Matter linked' },
  link: { kind: 'link', label: 'Linked' },
  unlink: { kind: 'unlink', label: 'Unlinked' },
  draft_attach: { kind: 'link', label: 'Linked to matter' },
  insight_attach: { kind: 'link', label: 'Linked to matter' },
  draft_detach: { kind: 'unlink', label: 'Unlinked from matter' },
  insight_detach: { kind: 'unlink', label: 'Unlinked from matter' },
  share: { kind: 'share', label: 'Shared' },
  shared: { kind: 'share', label: 'Shared' },
  review_started: { kind: 'created', label: 'Review started' },
  review_finalized: { kind: 'completed', label: 'Review finalized' },
  review_renamed: { kind: 'updated', label: 'Renamed' },
  review_status_changed: { kind: 'updated', label: 'Status changed' },
  finding_accepted: { kind: 'completed', label: 'Finding accepted' },
  finding_rejected: { kind: 'deleted', label: 'Finding rejected' },
  finding_modified: { kind: 'updated', label: 'Finding modified' },
  finding_escalated: { kind: 'updated', label: 'Finding escalated' },
  finding_decision_undone: { kind: 'restored', label: 'Decision undone' },
  bulk_accept: { kind: 'completed', label: 'Findings accepted' },
  bulk_reject: { kind: 'deleted', label: 'Findings rejected' },
  draft_generate: { kind: 'generated', label: 'Generated' },
  insight_generate: { kind: 'generated', label: 'Generated' },
  draft_download: { kind: 'document', label: 'Downloaded' },
  insight_download: { kind: 'document', label: 'Downloaded' },
};

export const titleizeAction = (s: string): string =>
  (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Best guess for an action the table does not know.
 *
 * Access first: `collaborator_remove` is about access, not deletion, and the
 * naive "contains remove" rule painted it as a destructive red row.
 */
export const inferActivityKind = (rawAction: string): ActivityKind => {
  const a = (rawAction || '').toLowerCase();
  if (!a) return 'other';
  if (/collaborator|access|permission|role|invit|assign|revoke|member/.test(a)) return 'access';
  if (/comment/.test(a)) return 'comment';
  if (/attach|file_upload|file_delete/.test(a)) return 'attachment';
  if (/archiv/.test(a)) return 'archived';
  if (/restor|undo|revert/.test(a)) return 'restored';
  if (/generat/.test(a)) return 'generated';
  if (/complet|finaliz|approv|accept/.test(a)) return 'completed';
  if (/shar/.test(a)) return 'share';
  if (/unlink|detach|disconnect/.test(a)) return 'unlink';
  if (/link|connect/.test(a)) return 'link';
  if (/upload|download|document|file/.test(a)) return 'document';
  if (/delet|remove|trash|reject|purge/.test(a)) return 'deleted';
  if (/creat|add|new|start/.test(a)) return 'created';
  if (/updat|chang|renam|edit|set_|_set|save|modif|move/.test(a)) return 'updated';
  return 'other';
};

const specFor = (rawAction: string): ActionSpec =>
  ACTIONS[rawAction] || {
    kind: inferActivityKind(rawAction),
    label: titleizeAction(rawAction),
  };

/** The visual family for a raw backend action. */
export const activityKind = (rawAction: string): ActivityKind => specFor(rawAction).kind;

/** The human verb for a raw backend action. */
export const activityActionLabel = (rawAction: string): string => specFor(rawAction).label;

// ─── Reading a row's subject ─────────────────────────────────────────────

/**
 * Some older audit rows stored a log-masking artifact — `[encoded:<base64>]`,
 * from sanitize_for_logging leaking into a stored note. Decode it back to
 * plaintext for display; drop the wrapper if it cannot be decoded.
 */
export const decodeEncodedTokens = (s: string): string =>
  s.replace(/\[encoded:([A-Za-z0-9+/=]+)\]/g, (_m, b64) => {
    try {
      return typeof atob === 'function' ? atob(b64) : '…';
    } catch {
      return '…';
    }
  });

/**
 * Known leading "category" segments on drafting document_type_ids
 * (e.g. contracts_purchase_agreement). Dropped for display so the activity
 * reads "Purchase Agreement" rather than "Contracts Purchase Agreement".
 */
const DOC_CATEGORY_PREFIXES = new Set([
  'contract', 'contracts', 'agreement', 'agreements', 'memo', 'memos',
  'letter', 'letters', 'brief', 'briefs', 'notice', 'notices', 'filing',
  'filings', 'motion', 'motions', 'pleading', 'pleadings', 'opinion',
  'opinions', 'form', 'forms', 'correspondence', 'clause', 'clauses',
  'policy', 'policies',
]);

export const humanizeDocType = (id?: string | null): string => {
  if (!id) return '';
  if (isOpaqueId(id)) return '';
  const parts = String(id).split('_').filter(Boolean);
  const rest =
    parts.length > 1 && DOC_CATEGORY_PREFIXES.has(parts[0].toLowerCase())
      ? parts.slice(1)
      : parts;
  /*
    Drop the synthetic `template_` prefix and any id tokens under it.

    A draft built on a user's own structure template carries the
    document_type_id `template_<uuid>` (ChooseTemplateStep.selectOwn), so the
    naive de-slug printed `Template 3f2a1b4c-9d8e-…` as the subject of every
    Created/Generated row — a raw uuid, on the activity rail, as the thing the
    row is about. Returning '' instead lets `describeAuditRecord` fall through
    to the next reading, and the row says just "Created".
  */
  const body = rest[0]?.toLowerCase() === 'template' ? rest.slice(1) : rest;
  const words = body.filter((part) => !isOpaqueId(part));
  if (words.length === 0) return '';
  return titleizeAction(words.join(' '));
};

/**
 * Drafting rows carry structured after_data/before_data rather than `details`
 * — surface the human-meaningful subject (doc type / filename) per action.
 */
const describeDraftingRecord = (record: AuditRecord): string | null => {
  const a = record.action || '';
  const after = (record.after_data || {}) as Record<string, any>;
  const before = (record.before_data || {}) as Record<string, any>;
  if (a.endsWith('_create') || a.endsWith('_generate')) {
    return humanizeDocType(after.document_type_id) || null;
  }
  if (a === 'draft_file_upload') return after.filename || null;
  if (a === 'draft_file_delete') return before.filename || null;
  if (a.endsWith('_download')) {
    const fmt = String(after.format || '').toLowerCase();
    return fmt === 'docx' ? 'Word document' : fmt ? fmt.toUpperCase() : null;
  }
  return null;
};

/**
 * One-line summary for known review activity actions, so the panel never
 * dumps raw JSON at the user.
 */
const humanizeDetails = (action: string, details: Record<string, any>): string | null => {
  const a = (action || '').toLowerCase();
  const name = details.name || details.collaborator_name || details.email || '';
  const clause = details.clause_type ? titleizeAction(details.clause_type) : '';
  switch (a) {
    case 'collaborator_added':
      return [name, details.access ? `${details.access} access` : null].filter(Boolean).join(' · ') || null;
    case 'collaborator_removed':
      return name || null;
    case 'collaborator_access_changed':
    case 'collaborator_role_update':
      return details.old_access && details.new_access
        ? `${name ? name + ': ' : ''}${details.old_access} → ${details.new_access}`
        : name || null;
    case 'review_started':
      return (
        [
          details.name,
          details.document_count != null
            ? `${details.document_count} ${details.document_count === 1 ? 'document' : 'documents'}`
            : null,
        ]
          .filter(Boolean)
          .join(' · ') || null
      );
    case 'review_renamed':
      return details.new_name || details.name || null;
    case 'finding_accepted':
    case 'finding_rejected':
    case 'finding_modified':
    case 'finding_escalated':
    case 'finding_decision_undone':
      return clause || null;
    case 'bulk_accept':
    case 'bulk_reject':
      return details.count != null ? `${details.count} findings` : null;
    case 'review_status_changed':
      return details.to ? titleizeAction(String(details.to)) : null;
    default:
      return null;
  }
};

/** Clean fallback for unknown detail objects — "Key: value", skipping ids. */
const summarizeObject = (obj: Record<string, any>): string =>
  Object.entries(obj)
    .filter(([k, v]) => v != null && v !== '' && !/(uuid|_id|^id)$/i.test(k))
    .map(([k, v]) => `${titleizeAction(k)}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' · ');

/** The diff array, wherever this particular endpoint decided to put it. */
export const auditDifferences = (record: AuditRecord): AuditDiff[] => {
  if (Array.isArray(record.differences)) return record.differences as AuditDiff[];
  if (record.differences && typeof record.differences === 'object') {
    const inner = (record.differences as any).differences;
    if (Array.isArray(inner)) return inner as AuditDiff[];
  }
  const dj = record.details_json;
  if (dj && typeof dj === 'object' && Array.isArray((dj as any).differences)) {
    return (dj as any).differences as AuditDiff[];
  }
  return [];
};

const summarizeDiffs = (diffs: AuditDiff[]): string =>
  diffs
    .map((d) => {
      const label = d.label || titleizeAction(d.field || '');
      if (d.from != null && d.to != null) return `${label}: ${String(d.from)} → ${String(d.to)}`;
      if (d.to != null) return `${label}: ${String(d.to)}`;
      return label;
    })
    .filter(Boolean)
    .join(' · ');

/** What the row is *about* — the line under the verb. */
export const describeAuditRecord = (record: AuditRecord): string | undefined => {
  const action = record.action || '';

  if (action.startsWith('draft_') || action.startsWith('insight_')) {
    const draftDetail = describeDraftingRecord(record);
    if (draftDetail) return draftDetail;
  }

  if (typeof record.details === 'string') {
    const trimmed = record.details.trim();
    if (trimmed) return decodeEncodedTokens(trimmed);
  } else if (record.details && typeof record.details === 'object') {
    const detailsObj = record.details as Record<string, any>;
    const summary = humanizeDetails(action, detailsObj) || summarizeObject(detailsObj);
    if (summary) return decodeEncodedTokens(summary);
  }

  const diffs = auditDifferences(record);
  if (diffs.length > 0) {
    const summary = summarizeDiffs(diffs);
    if (summary) return summary;
  }

  if (record.notes) {
    const notesStr = typeof record.notes === 'string' ? record.notes : JSON.stringify(record.notes);
    if (notesStr.trim()) return decodeEncodedTokens(notesStr.trim());
  }

  return undefined;
};

/**
 * Initials for an avatar disc, from a display name.
 *
 * First and last word, so "Ada Byron King" reads AK rather than AB — the
 * middle name is the part a person is least likely to be recognised by.
 */
export const actorInitials = (actor: string): string => {
  const words = (actor || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const first = words[0][0] || '';
  const last = words.length > 1 ? words[words.length - 1][0] || '' : '';
  return `${first}${last}`.toUpperCase();
};

/** Display name of whoever performed the action; '' when unattributed. */
export const auditActorName = (record: AuditRecord): string =>
  (record.user_name && record.user_name.trim()) ||
  `${record.user_first_name || ''} ${record.user_last_name || ''}`.trim();

/** THE mapper: one raw audit row → one timeline entry. */
export const toActivityEntry = (record: AuditRecord, index = 0): ActivityEntry => {
  const rawAction = record.action || '';
  const spec = specFor(rawAction);
  return {
    id: record.id != null ? String(record.id) : `activity-${index}`,
    actor: auditActorName(record),
    action: spec.label,
    target: describeAuditRecord(record),
    timestamp: record.timestamp || record.created_at || undefined,
    kind: spec.kind,
    raw: record,
  };
};

export const toActivityEntries = (records: AuditRecord[] | null | undefined): ActivityEntry[] =>
  (records || []).map(toActivityEntry);

// ─── Feed posts as activity ──────────────────────────────────────────────
/**
 * A feed post is an ActivityEntry too.
 *
 * The Feed and the Activity pane are two different STORES: the feed is its own
 * SQL table written by `POST /tasks/task/{uuid}/feed`, while Activity reads the
 * audit trail. Nothing on the backend writes across, so posting to the feed
 * left the Activity pane silent — which reads as a lost post rather than as a
 * boundary between two records.
 *
 * Until the backend emits an audit row on feed post (see the backend gap noted
 * on the task-4 card), the merge happens here: a host that already holds the
 * feed maps it through this and hands the result to the panel's `extraEntries`.
 * Pure, so the shape of a feed row is read in exactly one place.
 */
export interface FeedActivitySource {
  id?: string | number;
  event_type?: string;
  message?: string;
  timestamp?: string;
  created_by_name?: string;
  created_by?: { first_name?: string; last_name?: string; email?: string } | null;
  comments?: unknown[] | null;
}

/** Display name of a feed post's author; '' when the row names no one. */
export const feedActorName = (event: FeedActivitySource): string => {
  if (event.created_by_name && event.created_by_name.trim()) return event.created_by_name.trim();
  const by = event.created_by;
  if (!by) return '';
  const joined = `${by.first_name || ''} ${by.last_name || ''}`.trim();
  return joined || (by.email || '').trim();
};

/**
 * One feed post → one timeline entry.
 *
 * `kind` is always `comment`: every one of these is something a PERSON wrote,
 * which is exactly the distinction the glyph and the badge colour carry. The
 * post type ("update", "issue") becomes the verb, so the row still says which
 * kind of note it was.
 */
export const toFeedActivityEntry = (event: FeedActivitySource, index = 0): ActivityEntry => ({
  id: `feed-${event.id != null ? String(event.id) : index}`,
  actor: feedActorName(event),
  action: event.event_type ? titleizeAction(event.event_type) : 'Posted',
  target: (event.message || '').trim() || undefined,
  timestamp: event.timestamp || undefined,
  kind: 'comment',
  raw: event,
});

export const toFeedActivityEntries = (
  events: FeedActivitySource[] | null | undefined
): ActivityEntry[] => (events || []).map(toFeedActivityEntry);

/**
 * Newest first across BOTH stores.
 *
 * Undated rows keep their relative order at the front rather than being
 * scattered: a post still in flight has no stamp yet and must not sink below
 * a week-old audit row.
 */
export const mergeActivityEntries = (
  ...lists: (ActivityEntry[] | null | undefined)[]
): ActivityEntry[] => {
  const all = lists.flatMap((list) => list || []);
  const time = (entry: ActivityEntry): number => {
    if (!entry.timestamp) return Number.POSITIVE_INFINITY;
    const parsed = new Date(
      /Z$|[+-]\d{2}:\d{2}$/.test(entry.timestamp.trim())
        ? entry.timestamp.trim()
        : entry.timestamp.trim().replace(' ', 'T') + 'Z'
    ).getTime();
    return isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
  };
  return [...all].sort((a, b) => time(b) - time(a));
};

/**
 * The audit endpoints do not agree on a payload shape — entries live under
 * `entries`, `data`, `items` or `audit_log`, and "is there another page?" is
 * spelled three different ways. Normalize once.
 */
export const normalizeAuditPayload = (
  raw: any
): { entries: AuditRecord[]; hasNext: boolean } => {
  if (!raw) return { entries: [], hasNext: false };
  const list = raw.entries || raw.data || raw.items || raw.audit_log;
  const entries: AuditRecord[] = Array.isArray(list) ? list : Array.isArray(raw) ? raw : [];
  const pg = raw.pagination || {};
  let hasNext = false;
  if (typeof pg.has_next === 'boolean') {
    hasNext = pg.has_next;
  } else if (typeof raw.total_pages === 'number' && typeof raw.page === 'number') {
    hasNext = raw.page < raw.total_pages;
  } else if (
    typeof raw.total === 'number' &&
    typeof raw.per_page === 'number' &&
    typeof raw.page === 'number'
  ) {
    hasNext = raw.page * raw.per_page < raw.total;
  }
  return { entries, hasNext };
};
